import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import { buildPublicFileUrl } from '../../config/env';
import { fetchExitEntryRequests, updateExitEntryRequest } from '../../services/exitEntryApi';
import { DetailField, LoadingState, PageHeader, PageLayout, SectionCard, StatusBadge } from '../../components/ui';

const visaOptions = ['choose', 'One Visit', 'Multiple Visits'];

const initialFormData = {
  validity_from_date: '',
  validity_to_date: '',
  visa_type: 'choose',
  country: '',
  phone_number: '',
  location_in_country: '',
  period_in_days: '',
  reason: '',
  document: null,
};

const formatDateInput = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const getInclusiveDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return Math.floor((endDay - startDay) / 86400000) + 1;
};

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ExitEntryEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState(initialFormData);
  const [originalRequest, setOriginalRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  const calculatedDays = getInclusiveDaysBetween(formData.validity_from_date, formData.validity_to_date);
  const selectedVisaType = formData.visa_type === 'choose' ? '-' : formData.visa_type;

  const requiredStatus = useMemo(
    () => [
      { label: 'From date', done: Boolean(formData.validity_from_date) },
      { label: 'To date', done: Boolean(formData.validity_to_date) },
      { label: 'Visa type', done: formData.visa_type !== 'choose' },
    ],
    [formData.validity_from_date, formData.validity_to_date, formData.visa_type]
  );

  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  useEffect(() => {
    const loadRequest = async () => {
      setLoading(true);
      setError('');
      try {
        const all = await fetchExitEntryRequests();
        const current = Array.isArray(all)
          ? all.find((request) => Number(request.id) === Number(id))
          : null;

        if (!current) {
          setError('Could not find this exit / entry request.');
          return;
        }

        setOriginalRequest(current);
        setFormData({
          validity_from_date: formatDateInput(current.validity_from_date),
          validity_to_date: formatDateInput(current.validity_to_date),
          visa_type: current.visa_type || 'choose',
          country: current.country || '',
          phone_number: current.phone_number || '',
          location_in_country: current.location_in_country || '',
          period_in_days: current.period_in_days || '',
          reason: current.reason || '',
          document: null,
        });
      } catch (err) {
        console.error(err);
        setError('Failed to load the request.');
        toast.error('Failed to load the request');
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
  }, [id]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'document') {
      setFormData((prev) => ({ ...prev, document: files?.[0] || null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    if (!originalRequest) return;
    setValidationErrors([]);
    setError('');
    setFormData({
      validity_from_date: formatDateInput(originalRequest.validity_from_date),
      validity_to_date: formatDateInput(originalRequest.validity_to_date),
      visa_type: originalRequest.visa_type || 'choose',
      country: originalRequest.country || '',
      phone_number: originalRequest.phone_number || '',
      location_in_country: originalRequest.location_in_country || '',
      period_in_days: originalRequest.period_in_days || '',
      reason: originalRequest.reason || '',
      document: null,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setValidationErrors([]);

    try {
      await updateExitEntryRequest(id, formData);
      toast.success('Exit/Entry Request updated!');
      navigate('/exit-entry/list');
    } catch (err) {
      console.error('Error updating request:', err);
      if (err.response?.status === 422 && err.response.data?.errors) {
        const serverErrors = err.response.data.errors;
        const messages = [];
        Object.keys(serverErrors).forEach((field) => {
          serverErrors[field].forEach((message) => messages.push(message));
        });
        setValidationErrors(messages);
      } else {
        setError(err?.response?.data?.message || 'Failed to update request.');
      }
      toast.error('Failed to update request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Exit / entry"
        title={`Edit Exit / Entry Request #${id}`}
        subtitle="Update travel validity, visa details, destination contact information, and supporting documents."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/exit-entry/list')}
          >
            Back to List
          </Button>,
        ]}
      />

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <SectionCard
            tone="dashboard"
            title="Travel Details"
            subheader="Required fields are dates and visa type. Optional details help HR review faster."
            contentSx={{ p: { xs: 1.35, md: 1.7 } }}
          >
            <Stack spacing={1.8}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              {validationErrors.length ? (
                <Alert severity="error">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Please fix the following:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
                    {validationErrors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Box>
                </Alert>
              ) : null}

              <FieldGroup
                icon={<CalendarMonthRoundedIcon color="primary" />}
                title="Validity Window"
                description="Set the travel validity period for this exit / entry request."
              >
                <FieldGrid>
                  <TextField
                    label="Validity From Date"
                    type="date"
                    name="validity_from_date"
                    value={formData.validity_from_date}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Validity To Date"
                    type="date"
                    name="validity_to_date"
                    value={formData.validity_to_date}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </FieldGrid>
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<FlightTakeoffRoundedIcon color="primary" />}
                title="Visa and Destination"
                description="Keep visa and destination contact fields accurate for HR processing."
              >
                <FieldGrid>
                  <FormControl fullWidth required size="small" sx={fieldSx}>
                    <InputLabel id="visa-type-label">Visa Type</InputLabel>
                    <Select
                      labelId="visa-type-label"
                      name="visa_type"
                      value={formData.visa_type}
                      label="Visa Type"
                      onChange={handleChange}
                    >
                      {visaOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option === 'choose' ? 'Choose visa type' : option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Period in Days"
                    type="number"
                    name="period_in_days"
                    value={formData.period_in_days}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                    helperText={calculatedDays ? `Date range equals ${calculatedDays} day(s).` : 'Optional'}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    sx={fieldSx}
                  />
                </FieldGrid>
                <TextField
                  label="Location in Country"
                  name="location_in_country"
                  value={formData.location_in_country}
                  onChange={handleChange}
                  sx={{ mt: 1.2, ...fieldSx }}
                />
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<DescriptionRoundedIcon color="primary" />}
                title="Reason and Documents"
                description="Update request notes and optionally replace the supporting document."
              >
                <TextField
                  label="Reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  multiline
                  minRows={4}
                  sx={fieldSx}
                />
                <Stack spacing={1} sx={{ mt: 1.2 }}>
                  {originalRequest?.document_path ? (
                    <DetailField
                      label="Current Document"
                      value={
                        <Link
                          href={buildPublicFileUrl(originalRequest.document_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 850 }}
                        >
                          View current document
                        </Link>
                      }
                      icon={<AttachFileRoundedIcon fontSize="small" />}
                    />
                  ) : null}
                  <Button component="label" variant="outlined" startIcon={<AttachFileRoundedIcon />} sx={{ alignSelf: 'flex-start' }}>
                    {formData.document ? formData.document.name : 'Upload New Document'}
                    <input
                      hidden
                      type="file"
                      name="document"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleChange}
                    />
                  </Button>
                  {formData.document ? (
                    <Typography variant="caption" color="text.secondary">
                      Selected file: {formData.document.name}
                    </Typography>
                  ) : null}
                </Stack>
              </FieldGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  disabled={saving || Boolean(error && !originalRequest)}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
            <SectionCard
              tone="dashboard"
              title="Request Summary"
              subheader="Live overview of the edited request."
              contentSx={{ p: 1.6 }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={completionPct === 100 ? 'success' : 'warning'}
                    label={`${completionPct}% complete`}
                  />
                  {originalRequest?.status ? <StatusBadge status={originalRequest.status} /> : null}
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completionPct}
                  sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
                <SummaryRow
                  icon={<FlightTakeoffRoundedIcon fontSize="small" />}
                  label="Visa Type"
                  value={selectedVisaType}
                />
                <SummaryRow
                  icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  label="Validity"
                  value={`${formData.validity_from_date || '-'} to ${formData.validity_to_date || '-'}`}
                />
                <SummaryRow
                  icon={<ChecklistRoundedIcon fontSize="small" />}
                  label="Calculated Days"
                  value={calculatedDays ? `${calculatedDays} day(s)` : '-'}
                />
                <SummaryRow
                  icon={<PublicRoundedIcon fontSize="small" />}
                  label="Country"
                  value={formData.country}
                />
                <SummaryRow
                  icon={<PhoneIphoneRoundedIcon fontSize="small" />}
                  label="Phone"
                  value={formData.phone_number}
                />
                <SummaryRow
                  icon={<PlaceRoundedIcon fontSize="small" />}
                  label="Location"
                  value={formData.location_in_country}
                />
              </Stack>
            </SectionCard>
          </Stack>
        </Box>
      )}
    </PageLayout>
  );
}
